import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Mic, MicOff, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Floating voice-to-order button. Records the user's speech, transcribes via
 * /api/voice/transcribe, then calls /api/voice/parse-order to get a structured
 * draft, and invokes `onDraft(draft)` so the parent can prefill the form.
 *
 * Usage: <VoiceOrderButton onDraft={(d) => setForm(mergeDraft(d))} />
 */
export default function VoiceOrderButton({ onDraft, label = 'Voice', compact = false }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    if (busy || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        try {
          stream.getTracks().forEach(t => t.stop());
          setRecording(false);
          setBusy(true);
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          if (blob.size < 2000) { toast.error('Too short — hold the mic for at least 1 second'); return; }
          // 1) Transcribe
          const fd = new FormData();
          fd.append('file', blob, 'voice-order.webm');
          const tr = await axios.post(`${API}/voice/transcribe`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
          const transcript = (tr.data?.text || '').trim();
          if (!transcript) { toast.error('Could not understand audio'); return; }
          toast(`📝 "${transcript}"`);
          // 2) Parse to order draft
          const pr = await axios.post(`${API}/voice/parse-order`, { transcript }, { withCredentials: true });
          const draft = pr.data;
          if (!draft?.client_name && !(draft?.items || []).length) {
            toast.error('Could not extract order details. Try: "Create sale for ACME, 50 kg of Garam Masala"');
            return;
          }
          toast.success(`Prefilled from voice — ${draft.items?.length || 0} item(s)`);
          onDraft?.(draft);
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Voice order failed');
        } finally { setBusy(false); }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (err) {
      toast.error('Microphone access denied');
    }
  };

  const stop = () => {
    try { mediaRef.current?.stop(); } catch {}
  };

  useEffect(() => () => { try { mediaRef.current?.stream?.getTracks().forEach(t => t.stop()); } catch {} }, []);

  return (
    <button
      type="button"
      data-testid="voice-order-button"
      onClick={recording ? stop : start}
      disabled={busy}
      title={recording ? 'Tap to stop & transcribe' : 'Tap to record a sales order by voice'}
      className={`inline-flex items-center gap-1.5 ${compact ? 'px-2 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'} rounded-lg font-semibold border transition-all ${
        recording
          ? 'bg-rose-600 text-white border-rose-500 animate-pulse'
          : busy
            ? 'bg-zinc-800 text-zinc-500 border-zinc-700 cursor-wait'
            : 'bg-gradient-to-br from-amber-500/20 to-sky-500/20 text-amber-200 border-amber-500/50 hover:from-amber-500/30 hover:to-sky-500/30'
      }`}
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : recording ? <MicOff size={12} /> : <Mic size={12} />}
      {busy ? 'Parsing…' : recording ? 'Stop' : label}
      {!recording && !busy && <Sparkles size={10} className="opacity-70" />}
    </button>
  );
}
